import React, { useRef, useState } from "react";
import { sectionFrame } from "../../config/site";
import { recapVideos } from "../../content/site";
import { trackEvent } from "../../lib/analytics";
import { SectionHeading } from "../ui/SectionHeading";

type RecapVideo = (typeof recapVideos)[number];

function RecapPlayer({
    video,
    onPlay,
}: {
    video: RecapVideo;
    onPlay: (player: HTMLVideoElement) => void;
}) {
    const [activated, setActivated] = useState(false);
    const [failed, setFailed] = useState(false);
    const tracked = useRef(false);

    return (
        <article className="min-w-0">
            <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/20 bg-[#111] sm:rounded-3xl">
                {failed ? (
                    <div
                        role="alert"
                        className="flex h-full flex-col items-center justify-center gap-4 p-5 text-center"
                    >
                        <p>Не удалось воспроизвести видео.</p>
                        <a
                            className="text-accent underline underline-offset-4"
                            href={video.fallbackUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Смотреть на Яндекс Диске ↗
                        </a>
                    </div>
                ) : activated ? (
                    <video
                        className="h-full w-full"
                        aria-label={video.title}
                        src={video.src}
                        poster={video.poster}
                        controls
                        playsInline
                        autoPlay
                        preload="none"
                        onError={() => setFailed(true)}
                        onPlay={(event) => {
                            onPlay(event.currentTarget);
                            if (!tracked.current) {
                                tracked.current = true;
                                trackEvent("recap_video_play", { video: video.id });
                            }
                        }}
                    />
                ) : (
                    <button
                        type="button"
                        className="group relative h-full w-full focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-[-4px] focus-visible:outline-accent"
                        aria-label={`Смотреть видео «${video.title}»`}
                        onClick={() => setActivated(true)}
                    >
                        <img
                            src={video.poster}
                            alt=""
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                            loading="lazy"
                        />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <span
                                className="flex size-16 items-center justify-center rounded-full bg-accent text-black shadow-lg transition group-hover:scale-110 sm:size-20"
                                aria-hidden="true"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="ml-1 size-7"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </span>
                        </span>
                    </button>
                )}
            </div>
            <h3 className="mt-4 text-xl font-semibold sm:text-2xl">{video.title}</h3>
        </article>
    );
}

export function RecapSection() {
    const container = useRef<HTMLElement>(null);
    const handlePlay = (activePlayer: HTMLVideoElement) => {
        container.current?.querySelectorAll("video").forEach((player) => {
            if (player !== activePlayer) player.pause();
        });
    };

    return (
        <section
            id="recap"
            ref={container}
            className="relative z-10 scroll-mt-24 px-4 pt-6 sm:px-6"
        >
            <div className={sectionFrame}>
                <SectionHeading label="Первая DriveeCon · 2026" title="Как это было" />
                <div className="grid gap-8 lg:grid-cols-2">
                    {recapVideos.map((video) => (
                        <RecapPlayer key={video.id} video={video} onPlay={handlePlay} />
                    ))}
                </div>
            </div>
        </section>
    );
}
